import { UI, WebViewerInstance } from "@pdftron/webviewer";


/**
 * Type ThemeMode can be PDFTron's value of *UI.Theme*, "light", "dark", or enum *Theme*.
 *
 * For some reason, PDFTron's theme.LIGHT and theme.DARK returns the type 'string' instead of strict "light" and "dark",
 * which makes the final type equal to 'string.
 * ```
 * enum Theme {
 *  Light = 'light',
 *  LIGHT = 'light',
 *  Dark = 'dark',
 *  DARK = 'dark',
 * }
 * ```
 */
export type ThemeMode = typeof UI.Theme["LIGHT"] | typeof UI.Theme["DARK"] | "light" | "dark";
/**
 * Contains string enumeration for LIGHT and DARK theme for WebViewer. They are used to set the theme of Viewer.
 *
 * Comparable to PDFTron's *UI.Theme*.
 */
export const enum Theme {
    // Please make sure that the string value is the same as PDFTron's 'UI.Theme' type.
    // As of PDFTron v8.12.0, theme.LIGHT = 'light' and theme.DARK = 'dark'
    /** Theme of the viewer: Light mode */
    Light = 'light',
    /** Theme of the viewer: Dark mode */
    Dark = 'dark',
}

/**
 * Contains the name of the theme for WebViewer. They are used as a name of a 'switch theme mode' button.
 */
export const enum ThemeLabel {
    LightMode = 'Light Mode',
    DarkMode = 'Dark Mode',
}

/**
 * Contains the path (string) of icons for each button.
 */
export const enum PDFTronIcons {
    // DarkMode
    DarkModeIcon = '/assets/icons/light_mode_white_48dp.svg',
    PrintDarkModeIcon = '/assets/icons/print_white_48dp.svg',
    DownloadDarkModeIcon = '/assets/icons/file_download_white_48dp.svg',
    SaveDarkModeIcon = '/assets/icons/save_white_48dp.svg',
    AddPageDarkModeIcon = '/assets/icons/post_add_white_48dp.svg',
    InsertFileDarkModeIcon = '/assets/icons/insert_drive_file_white_48dp.svg',
    SwitchViewerDarkModeIcon = '/assets/icons/edit_white_24dp.svg',
    AddTemplateDarkModeIcon = '/assets/icons/add_pdf_template_white.png',
    // LightMode
    LightModeIcon = '/assets/icons/dark_mode_black_48dp.svg',
    PrintLightModeIcon = '/assets/icons/print_black_48dp.svg',
    DownloadLightModeIcon = '/assets/icons/file_download_black_48dp.svg',
    SaveLightModeIcon = '/assets/icons/save_black_48dp.svg',
    AddPageLightModeIcon = '/assets/icons/post_add_black_48dp.svg',
    InsertFileLightModeIcon = '/assets/icons/insert_drive_file_black_48dp.svg',
    SwitchViewerLightModeIcon = '/assets/icons/edit_black_24dp.svg',
    AddTemplateLightModeIcon = '/assets/icons/add_pdf_template_black.png',
}

/**
 * Set WebViewer's CSS properties to be Dark theme.
 * @param instance WebViewerInstance
 */
export function setWebViewerDarkMode(instance: WebViewerInstance) {
    const style = instance?.UI.iframeWindow.document.documentElement.style;
    style.setProperty(`--blue-1`, '#192530');
    style.setProperty(`--blue-2`, '#1B2B3A');
    style.setProperty(`--blue-3`, '#334250');
    style.setProperty(`--blue-4`, '#334758');
    style.setProperty(`--blue-5`, '#1A4971');
    style.setProperty(`--blue-6`, '#3183C8');

    style.setProperty(`--gray-0`, '#000000');
    style.setProperty(`--gray-1`, '#16181C');
    style.setProperty(`--gray-2`, '#21242A');
    style.setProperty(`--gray-3`, '#343A40');
    style.setProperty(`--gray-4`, '#485056');
    style.setProperty(`--gray-5`, '#868E96');
    style.setProperty(`--gray-6`, '#ADB5BD');
    style.setProperty(`--gray-7`, '#CFD4DA');
    style.setProperty(`--gray-8`, '#DEE2E6');
    style.setProperty(`--gray-9`, '#E7EBEE');
    style.setProperty(`--gray-10`, '#F1F3F5');
    style.setProperty(`--gray-11`, '#F8F9FA');
    style.setProperty(`--gray-12`, '#FFFFFF');

    style.setProperty(`--yellow-1`, '#e1b90080');
    style.setProperty(`--red`, 'red');
}

/**
 * Set WebViewer's CSS properties to be Light theme.
 * @param instance WebViewerInstance
 */
export function setWebViewerLightMode(instance: WebViewerInstance) {
    const style = instance?.UI.iframeWindow.document.documentElement.style;
    style.setProperty(`--blue-1`, '#E7EDF3');
    style.setProperty(`--blue-2`, '#DDE6EE');
    style.setProperty(`--blue-3`, '#CFD8E0');
    style.setProperty(`--blue-4`, '#CFD8E0');
    style.setProperty(`--blue-5`, '#3183C8');
    style.setProperty(`--blue-6`, '#1A4971');

    style.setProperty(`--gray-0`, '#FFFFFF');
    style.setProperty(`--gray-1`, '#F8F9FA');
    style.setProperty(`--gray-2`, '#F1F3F5');
    style.setProperty(`--gray-3`, '#E7EBEE');
    style.setProperty(`--gray-4`, '#DEE2E6');
    style.setProperty(`--gray-5`, '#CFD4DA');
    style.setProperty(`--gray-6`, '#ADB5BD');
    style.setProperty(`--gray-7`, '#868E96');
    style.setProperty(`--gray-8`, '#485056');
    style.setProperty(`--gray-9`, '#343A40');
    style.setProperty(`--gray-10`, '#21242A');
    style.setProperty(`--gray-11`, '#101214');
    style.setProperty(`--gray-12`, '#000000');

    style.setProperty(`--yellow-1`, '#FACE0080');
    style.setProperty(`--red`, 'red');
}
